import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { HinweisData, TourIdentifier } from '@data-table/data-table.types';
import { getAnomaliesBaseUrl } from '@legacy-modules/common/services/EnvironmentConfigurationService';
import { IRawAnomalyResponse } from '@legacy-modules/dashboard/anomalies/interfaces/IRawAnomalyResponse';
import { DateFormat } from '@legacy-modules/dashboard/components/table/DateTimeCell';
import { Duration } from '@legacy-modules/dashboard/models/enums/Duration';
import { knownAnomalyFilter } from '@legacy-modules/tour/utils/KnownAnomalyFilter';
import { selectTokenId } from '@redux/auth.selectors';
import { DurationUtils } from '@utils/duration-utils';

export const AnomalyReason = {
  'wzf.ht-zustellung': 'Außerhalb Wunschzeitfenster',
  'wzf.ht-abholung': 'Außerhalb Wunschzeitfenster',
  'wzf.atg-abholung': 'Außerhalb ATG-Abholzeitfenster',
  's.colli-teillieferung': 'Teillieferung (mehrcollig)',
  'usk.ruecklauf-am-tourende': 'Rücklauf am Tourende',
  'hr.zustellzeit': 'Außerhalb Hermes Zustellzeit',
  'uv.annahmeverweigerung-wunschtag': 'Rücklauf A am Wunschtag',
  'hr.zustelltag': 'Tagversetzte Bearbeitung',
  'uv.distanceviolation.DV250mNB': 'Distanzverstoß Nachbarschaftsabgabe',
  'uv.distanceviolation.DV100meA': 'Distanzverstoß Abholauftrag',
  'uv.distanceviolation.DV100mSEZ': 'Distanzverstoß Zustellung',
  'uv.distanceviolation.DV100mRL': 'Distanzverstoß Rücklauf',
  'uv.distanceviolation.DV1kmRL': 'Distanzverstoß Adresse',
  'uv.distanceviolation.DV100kmRL': 'Distanzverstoß ohne Zustellversuch',
};

type AnomalyGroup = { anomaly: HinweisData; count: number };

const queryFn = async (authToken: string, orgKey: string, from: string, to: string): Promise<IRawAnomalyResponse[]> => {
  const url = new URL(`${getAnomaliesBaseUrl()}/anomalies/find`);
  url.searchParams.set('token', authToken);
  url.searchParams.append('org-key', orgKey);
  url.searchParams.append('from', from);
  url.searchParams.append('to', to);
  return fetch(url).then((res) => res.json());
};

/**
 * Hook to fetch anomalies and transforms them to the expected table format
 * @param orgKey
 * @param duration
 * @param enabled
 */
const useHinweiseData = (orgKey: string, duration: Duration, enabled: boolean): [HinweisData[], boolean] => {
  const authToken = useSelector(selectTokenId);

  const dateRange = DurationUtils.getDateRange(duration);

  const { from, to } = {
    from: dateRange.from.format('YYYYMMDD'),
    to: dateRange.to.format('YYYYMMDD'),
  };

  const { data, isLoading } = useQuery<IRawAnomalyResponse[], unknown, HinweisData[]>({
    queryKey: ['anomalies', 'find', { orgKey, from, to }],
    queryFn: () => queryFn(authToken, orgKey, from, to),
    enabled: !!orgKey && !!duration && enabled,
    select: (data) => {
      // Group recurring anomalies of the same type and count them in order to set display details
      const groups = data?.filter(knownAnomalyFilter)?.reduce((acc, anomaly) => {
        const hinweisart = AnomalyReason[anomaly.anomalyType];
        const tourdatum = moment(anomaly?.tourIdentifier?.tourDate).format(DateFormat.Date);
        const groupKey = `${tourdatum}|${anomaly.tourIdentifier.tourNumber}|${hinweisart}`;

        if (acc.has(groupKey)) {
          acc.get(groupKey).count++;
        } else {
          const transformedAnomaly: HinweisData = {
            ...anomaly,
            details: undefined,
            tourIdentifier: {
              tourNumber: anomaly?.tourIdentifier?.tourNumber?.toString(),
              orgKey: null,
              orgId: anomaly?.tourIdentifier?.zsbOrgId?.toString(),
              tourDate: anomaly?.tourIdentifier?.tourDate,
              contractorKey: anomaly?.contractorKey,
            } as TourIdentifier,
            hinweisart,
            tourdatum,
          };
          acc.set(groupKey, {
            anomaly: transformedAnomaly,
            count: 1,
          });
        }
        return acc;
      }, new Map<string, AnomalyGroup>());

      return Array.from(groups.values()).map((anomalyGroup) => {
        const { anomaly, count } = anomalyGroup;
        let details: string;

        if (anomaly.anomalyLevel === 'tour') {
          details = `${count} Tourenbereich`;
        } else if (['wzf.ht-zustellung', 'wzf.ht-abholung', 'wzf.atg-abholung'].includes(anomaly.anomalyType)) {
          details = `${count} Zeitfenster`;
        } else if (
          [
            'uv.distanceviolation.DV250mNB',
            'uv.distanceviolation.DV100meA',
            'uv.distanceviolation.DV100mSEZ',
            'uv.distanceviolation.DV1kmRL',
            'uv.distanceviolation.DV100mRL',
            'uv.distanceviolation.DV100kmRL',
          ].includes(anomaly.anomalyType)
        ) {
          details = `${count} ${count <= 1 ? 'Verstoß' : 'Verstöße'}`;
        } else {
          const suffix = count === 1 ? 'Sendung' : 'Sendungen';
          details = `${count} ${suffix}`;
        }

        return { ...anomaly, details };
      });
    },
  });

  return [data || [], isLoading];
};

export default useHinweiseData;
